import React from 'react';
import { Grid, Paper, Typography, Box, Button } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import adcColumns from './AdcColumns';
import { useTheme } from '@mui/material/styles';
import { tokens } from '../../../theme'; // Adjust the import path as necessary

const AdcDataGrid = ({ adcRows, viewBotFilteredResults, viewRealTimeMetrics }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const columns = adcColumns({ viewBotFilteredResults, viewRealTimeMetrics });
  const handleExportCSV = () => {
    if (!adcRows?.length) return;

    // Prefer the grid's columns list so we export what users see
    const exportableCols = (columns || [])
      .filter((c) => c.field && c.field !== '__check__') // basic guard
      .map((c) => ({
        field: c.field,
        header: c.headerName || c.field,
      }));

    const headers = exportableCols.map((c) => c.header);

    const escapeCsv = (v) => {
      if (v == null) return '';
      const s = String(v);
      // quote if it contains CSV special chars
      if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
      return s;
    };

    const lines = [];
    lines.push(headers.map(escapeCsv).join(','));

    for (const r of adcRows) {
      const row = exportableCols.map(({ field }) => escapeCsv(r?.[field]));
      lines.push(row.join(','));
    }

    const csvContent = lines.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);

    const a = document.createElement('a');
    a.href = url;
    a.download = `adc_counts_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);

    URL.revokeObjectURL(url);
  };

  return (
    <>
      {/* Check if adcRows is present and not empty */}
      {adcRows && adcRows.length > 0 && (
        <>
          {/* New Grid component to display the sum and average values */}
          {/*<Grid container spacing={2}>
            {[
              { label: 'Total Visits', value: new Intl.NumberFormat().format(adcRows.reduce((sum, row) => sum + row.visits_count, 0)) },
              { label: 'Total Visits Resolved', value: new Intl.NumberFormat().format(adcRows.reduce((sum, row) => sum + row.visits_resolved_count, 0)) },
              { label: 'Total Distinct Visits', value: new Intl.NumberFormat().format(adcRows.reduce((sum, row) => sum + row.distinct_visits_count, 0)) },
              { label: 'Total Distinct Visits Resolved', value: new Intl.NumberFormat().format(adcRows.reduce((sum, row) => sum + row.distinct_visits_resolved_count, 0)) },
              { label: 'Total Distinct HEMs Delivered', value: new Intl.NumberFormat().format(adcRows.reduce((sum, row) => sum + row.distinct_hems_count, 0)) },
              { label: 'Average % Resolved/Day', value: `${(adcRows.reduce((sum, row) => sum + row.percent_resolved, 0) / adcRows.length * 100).toFixed(2)}%` },
              { label: 'Average Unique % Resolved/Day', value: `${(adcRows.reduce((sum, row) => sum + row.unique_percent_resolved, 0) / adcRows.length * 100).toFixed(2)}%` },
            ].map((item, index) => (
              <Grid item xs key={index}>
                <Paper
                  variant="outlined"
                  sx={{
                    borderRadius: 2,
                    padding: '10px',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Typography variant="subtitle2" color="text.secondary">
                    {item.label}
                  </Typography>
                  <Typography variant="h6">
                    {item.value}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>*/}

          {/* Existing DataGrid */}
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleExportCSV}
                  sx={{
                    borderColor: 'lightgreen',
                    color: 'lightgreen',
                    '&:hover': { borderColor: 'green', color: 'green' },
                  }}
                >
                  Export CSV
                </Button>
              </Box>
              <Box
                m="25px 0 0 0"
                height="45vh"
                sx={{
                  "& .MuiDataGrid-root": {
                    border: "none",
                  },
                  "& .MuiDataGrid-cell": {
                    borderBottom: "none",
                  },
                  "& .name-column--cell": {
                    color: colors.blueAccent[600],
                  },
                  "& .MuiDataGrid-columnHeaders": {
                    backgroundColor: colors.grey[700],
                    borderBottom: "none",
                  },
                  "& .MuiDataGrid-virtualScroller": {
                    backgroundColor: colors.primary[400],
                  },
                  "& .MuiDataGrid-footerContainer": {
                    borderTop: "none",
                    backgroundColor: colors.blueAccent[800],
                  },
                  "& .MuiCheckbox-root": {
                    color: `${colors.blueAccent[200]} !important`,
                  },
                }}
              >
                <DataGrid
                  rows={adcRows}
                  columns={columns}
                  //autoHeight
                  disableColumnMenu
                  disableSelectionOnClick
                  getRowId={(row) => row.on_date}
                  sortModel={[{ field: 'on_date', sort: 'desc' }]}
                //style={{ overflowY: 'scroll' }} // Ensure the DataGrid is scrollable
                />
              </Box>
            </Grid>
            <Grid item xs={6}>
              {/* Content for the second half of the split view */}
            </Grid>
          </Grid>
        </>
      )}

      {/* Display a message if adcRows is empty or not present */}
      {!adcRows || adcRows.length === 0 && (
        <Typography variant="subtitle1" color="text.secondary" style={{ marginTop: 16 }}>
          No ADC data available.
        </Typography>
      )}
    </>
  );
};

export default AdcDataGrid;
